import React, { useState, useEffect } from 'react';
import { Dialog, CircularProgress } from '@mui/material';
import OTPInput, { ResendOTP } from 'otp-input-react';
import { useValidateOTP, useResendOTP } from '../../hooks/useOTPValidation';

const OTPValidationModal = ({
  open,
  onClose,
  onSuccess,
  transactionData = {},
}) => {
  const [otp, setOTP] = useState('');
  const [error, setError] = useState('');

  const { mutate: validateOTP, isLoading: validatingOTP } = useValidateOTP(
    (data) => {
      setError('');
      onSuccess(data);
    },
    (err) => {
      setError(err?.response?.data?.message || 'Invalid OTP. Please try again.');
    }
  );

  const { mutate: resendOTP, isLoading: resendingOTP } = useResendOTP(
    () => {
      setOTP('');
      setError('');
    },
    (err) => {
      setError(err?.response?.data?.message || 'Failed to resend OTP. Please try again.');
    }
  );

  useEffect(() => {
    if (open) {
      setOTP('');
      setError('');
    }
  }, [open]);

  const handleValidateOTP = () => {
    if (!otp || otp.trim().length < 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    validateOTP({
      transactionId: transactionData?.transactionId,
      otp,
      pan: transactionData?.pan,
      dataSource: transactionData?.dataSource,
      updatedMobile: transactionData?.mobile,
    });
  };

  const handleResendOTP = () => {
    resendOTP({
      transactionId: transactionData?.transactionId,
      pan: transactionData?.pan,
      dob: transactionData?.dob,
      dataSource: transactionData?.dataSource,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '1.5rem',
          overflow: 'hidden',
          boxShadow: '0 50px 100px -20px rgba(0,0,0,0.25)',
        },
      }}
    >
      <div className="otp-content">
        {/* Close Button */}
        <button className="modal-close" onClick={onClose} type="button">
          <span className="material-symbols-outlined">close</span>
        </button>

        {/* Header */}
        <div className="otp-header">
          <div className="otp-icon-wrapper">
            <span className="material-symbols-outlined">verified_user</span>
          </div>
          <h2 className="otp-title">CKYC Verification</h2>
          <p className="otp-subtitle">
            An OTP has been sent to your registered mobile number linked with CKYC.
          </p>
          {(transactionData?.pan || transactionData?.dob) && (
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                justifyContent: 'center',
                marginTop: '0.75rem',
                flexWrap: 'wrap',
              }}
            >
              {transactionData?.pan && (
                <span className="modal-badge">PAN: {transactionData.pan}</span>
              )}
              {transactionData?.dob && (
                <span className="modal-badge">DOB: {transactionData.dob}</span>
              )}
            </div>
          )}
        </div>

        {/* OTP Form */}
        <form
          className="otp-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (otp.length === 6) handleValidateOTP();
          }}
        >
          <div className="otp-inputs">
            <OTPInput
              value={otp}
              onChange={(val) => {
                setOTP(val);
                setError('');
              }}
              autoFocus
              OTPLength={6}
              otpType="number"
              placeholder="······"
              disabled={validatingOTP}
              className="otp-input"
            />
          </div>

          {error && (
            <p
              style={{
                color: '#d32f2f',
                fontSize: '0.8rem',
                textAlign: 'center',
                marginTop: '0.5rem',
                marginBottom: 0,
              }}
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            className="submit-btn otp-submit"
            onClick={handleValidateOTP}
            disabled={validatingOTP || otp.length < 6}
          >
            {validatingOTP ? (
              <>
                <CircularProgress size={18} color="inherit" style={{ marginRight: 8 }} />
                Verifying...
              </>
            ) : (
              <>
                Verify &amp; Proceed
                <span className="material-symbols-outlined text-sm-icon ml-1">arrow_forward</span>
              </>
            )}
          </button>
        </form>

        {/* Resend */}
        <div className="otp-actions">
          <ResendOTP
            maxTime={120}
            onResendClick={handleResendOTP}
            style={{ display: 'flex', justifyContent: 'center' }}
            renderButton={({ remainingTime, onClick, disabled }) => (
              <button
                className="otp-resend"
                onClick={onClick}
                disabled={disabled || resendingOTP}
                type="button"
              >
                <span className="material-symbols-outlined text-sm-icon">history</span>
                {remainingTime !== 0
                  ? `Resend OTP in ${remainingTime}s`
                  : resendingOTP
                  ? 'Sending...'
                  : 'Resend OTP'}
              </button>
            )}
            renderTime={() => null}
          />
        </div>

        {/* Footer */}
        <div className="otp-security-note">Secure CKYC verification • Protected session</div>
      </div>
    </Dialog>
  );
};

export default OTPValidationModal;
